import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import {
  ExternalLink, Mail, TrendingUp, Flame, PenLine, BookOpen, Headphones,
  Check, ThumbsUp, ThumbsDown, Trash2, MoreHorizontal, Megaphone,
} from "lucide-react";
import { BACKEND_URL } from "../lib/backend";
import { labelFor } from "../lib/taxonomyLabels";
import { clampSentence } from "../lib/textUtils";

const API = `${BACKEND_URL}/api`;

const trackClick = (url) => {
  if (!url) return;
  // Fire-and-forget — must never block the navigation
  axios.post(`${API}/news/click`, { url }).catch(() => { /* ignore */ });
};

const RedditSnoo = ({ className }) => (
  <svg viewBox="0 0 20 20" className={className} fill="currentColor" aria-hidden="true">
    <circle cx="10" cy="10" r="10" fill="#FF4500" />
    <path fill="white" d="M16.67 10a1.46 1.46 0 0 0-2.47-1 7.12 7.12 0 0 0-3.85-1.23l.65-3.08 2.13.45a1 1 0 1 0 .14-.5l-2.38-.5a.25.25 0 0 0-.3.19l-.73 3.44a7.14 7.14 0 0 0-3.89 1.23 1.46 1.46 0 1 0-1.61 2.39 2.87 2.87 0 0 0 0 .44c0 2.24 2.61 4.06 5.83 4.06s5.83-1.82 5.83-4.06a2.87 2.87 0 0 0 0-.44 1.46 1.46 0 0 0 .56-1.39zM7.27 11a1 1 0 1 1 1 1 1 1 0 0 1-1-1zm5.58 2.65a3.56 3.56 0 0 1-2.85.86 3.56 3.56 0 0 1-2.85-.86.25.25 0 0 1 .35-.35 3.07 3.07 0 0 0 2.5.71 3.07 3.07 0 0 0 2.5-.71.25.25 0 0 1 .35.35zm-.13-1.65a1 1 0 1 1 1-1 1 1 0 0 1-1 1z" />
  </svg>
);

/**
 * NewsCard — reader card. `isAdmin` here means "Admin mode is ON" (redesign
 * item 2): when false (readers, or admins with the mode off) ZERO editorial
 * UI is rendered. When true: amber left border, status chip, and all
 * editorial actions collapsed into one "···" overflow menu.
 */
const NewsCard = ({ article, darkMode, isSelected, onSelect, isAdmin,
  onStarToBrief, onStarToNewsletter, onStarToPodcast,
  onUnstarBrief, onUnstarNewsletter, onUnstarPodcast, onRankFeedback, onDelete, onPrOverride }) => {
  const fmt = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const wordCount = article.summary?.split(/\s+/).filter(Boolean).length || 0;
  const readTime = Math.max(1, Math.ceil(wordCount / 200));
  const dm = darkMode;

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Aggregate queue membership
  const inBrief = !!article.in_brief_queue;
  const inNewsletter = !!article.in_newsletter_queue;
  const inPodcast = !!article.in_podcast_queue;
  const isStaged = inBrief || inNewsletter || inPodcast;

  // Layer A: admin ranking override state (promote/demote, 72h TTL)
  const rankOv = article.rank_override || null;
  const promoted = rankOv?.action === "promote";
  const demoted = rankOv?.action === "demote";
  // iter182: republished press release (detector or admin override) — minimum relevance, admin can say «non è PR»
  const isPr = !!article.is_pr;
  const prOverridden = !!article.pr_override;
  const ovLabel = rankOv
    ? `${promoted ? "Promoted" : "Demoted"} by ${rankOv.by || "admin"} · ${rankOv.at ? new Date(rankOv.at).toLocaleString() : ""}${rankOv.expires_at ? ` · expires ${new Date(rankOv.expires_at).toLocaleString()}` : ""}`
    : null;

  // Close overflow menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [menuOpen]);

  const menuAction = (handler, ...args) => (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    if (handler) handler(...args);
  };

  const isReddit =
    article.originalUrl?.includes("reddit.com") ||
    article.source?.toLowerCase().includes("reddit");

  // Tier-2 viral variant (redesign item 3): ≥3 distinct publishers
  const isViral = !!article.is_trending && article.trending_type === "viral" && !isReddit;
  const viralN = article.cluster_size || article.cluster_sources?.length || 0;

  // Extract subreddit name for display (e.g. "Reddit r/travel" → "r/travel")
  const subredditLabel = (() => {
    if (!isReddit) return null;
    const match = article.source?.match(/r\/\w+/i);
    if (match) return match[0];
    const urlMatch = article.originalUrl?.match(/reddit\.com\/r\/([^/]+)/i);
    return urlMatch ? `r/${urlMatch[1]}` : "Reddit";
  })();

  // Source logo: curated logo_url first, else Icon Horse favicon fallback
  const sourceLogoSrc = (() => {
    if (article.source_logo_url) return article.source_logo_url;
    try {
      const host = new URL(article.originalUrl).hostname.replace(/^www\./, "");
      return host ? `https://icon.horse/icon/${host}` : null;
    } catch {
      return null;
    }
  })();

  const menuItemCls = (danger = false) =>
    `w-full flex items-center gap-2 px-3 py-2 text-xs transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
      danger
        ? (dm ? "text-red-400 hover:bg-red-500/10" : "text-red-600 hover:bg-red-50")
        : (dm ? "text-slate-200 hover:bg-white/5" : "text-[#3b494c] hover:bg-[#f8f9fa]")
    }`;

  return (
    <div
      data-testid="news-card"
      className={`relative rounded-2xl overflow-hidden transition-all duration-200 group animate-fade-in
        ${isSelected ? "ring-2 ring-[#006874] ring-offset-2" : ""}
        ${isViral ? "border-t-[3px] border-t-[#b3541e]" : ""}
        ${isAdmin ? "border-l-4 border-l-[#d8a940]" : isReddit ? (dm ? "border-l-[3px] border-l-orange-500/60" : "border-l-[3px] border-l-orange-400") : ""}
        ${dm ? "bg-[#1a2122] hover:bg-[#1e2627]" : "bg-[#f3f4f5] hover:bg-[#ecedef]"}`}
    >
      {/* Reddit badge — absolute, top-left corner */}
      {isReddit && (
        <div className="absolute top-4 left-4 z-10 flex items-center gap-1.5 px-2 py-0.5 rounded-full"
          style={{ background: dm ? "rgba(255,69,0,0.12)" : "rgba(255,69,0,0.08)", border: "1px solid rgba(255,69,0,0.2)" }}>
          <RedditSnoo className="w-3 h-3 flex-shrink-0" />
          <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#FF4500" }}>
            {subredditLabel}
          </span>
        </div>
      )}

      {/* HOT badge (iter94) — viral AND core-relevant, computed server-side (is_hot) */}
      {article.is_hot && !isReddit && !article.is_trending && (
        <div
          data-testid="hot-badge"
          title="Hot: covered by multiple publishers AND on core topics (AI · innovation · travel tech)"
          className="absolute top-4 left-4 z-10 flex items-center gap-1 px-2 py-0.5 rounded-full"
          style={{
            background: dm ? "rgba(220,38,38,0.2)" : "rgba(220,38,38,0.13)",
            border: "1px solid rgba(220,38,38,0.4)",
          }}
        >
          <Flame className="w-3 h-3 flex-shrink-0" style={{ color: "#dc2626" }} />
          <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#b91c1c" }}>
            Hot
          </span>
        </div>
      )}

      {/* Trending badge — viral (cluster, ≥3 sources) takes priority over click-based early signal */}
      {article.is_trending && !isReddit && (
        article.trending_type === "viral" ? (
          <div
            data-testid="trending-badge-viral"
            title={`Viral story — covered by ${viralN || "multiple"} publishers`}
            className="absolute top-4 left-4 z-10 flex items-center gap-1 px-2 py-0.5 rounded-full"
            style={{
              background: dm ? "rgba(179,84,30,0.16)" : "#faeee6",
              border: "1px solid rgba(179,84,30,0.35)",
            }}
          >
            <Flame className="w-3 h-3 flex-shrink-0" style={{ color: "#b3541e" }} />
            <span className="text-[10px] font-bold uppercase tracking-wider flex items-center gap-1" style={{ color: dm ? "#e8956b" : "#b3541e" }}>
              Viral
              {viralN > 0 && (
                <span className="opacity-80">· {viralN} sources</span>
              )}
            </span>
          </div>
        ) : (
          <div
            data-testid="trending-badge"
            title="Trending: 4+ public clicks within 6h of publication"
            className="absolute top-4 left-4 z-10 flex items-center gap-1 px-2 py-0.5 rounded-full animate-pulse"
            style={{
              background: dm ? "rgba(249,115,22,0.18)" : "rgba(249,115,22,0.12)",
              border: "1px solid rgba(249,115,22,0.35)",
            }}
          >
            <Flame className="w-3 h-3 flex-shrink-0" style={{ color: "#f97316" }} />
            <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#ea580c" }}>
              Trending
            </span>
          </div>
        )
      )}

      {/* Newsletter "Used" badge — admin mode only */}
      {article.used_in_newsletter && isAdmin && (
        <div className="absolute z-10 top-12 right-4">
          <span data-testid="newsletter-used-badge" title="Already used in a newsletter"
            className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${dm ? "bg-amber-900/40 text-amber-400 border border-amber-700/40" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>
            <Mail className="w-2.5 h-2.5" /> Used
          </span>
        </div>
      )}

      {/* Select checkbox — admin mode only */}
      {isAdmin && (
        <div className="absolute top-4 right-4 z-10">
          <input type="checkbox" checked={isSelected} onChange={() => onSelect(article)} data-testid="article-checkbox"
            className={`w-4 h-4 rounded cursor-pointer accent-[#8a5a06] transition-opacity ${isSelected ? "opacity-100" : "opacity-40 group-hover:opacity-100"}`} />
        </div>
      )}

      {/* "···" overflow menu — ALL editorial actions live here (admin mode only) */}
      {isAdmin && (
        <div ref={menuRef} className="absolute top-3 right-10 z-20">
          <button
            onClick={(e) => { e.stopPropagation(); setMenuOpen(o => !o); }}
            data-testid="card-admin-menu-btn"
            aria-label="Editorial actions"
            title="Editorial actions (promote, demote, stage, delete)"
            className={`p-1.5 rounded-full transition-all ${
              menuOpen
                ? (dm ? "bg-amber-400/20 text-amber-300" : "bg-[#f7ecd2] text-[#8a5a06]")
                : (dm ? "text-amber-300/70 hover:text-amber-300 hover:bg-amber-400/10" : "text-[#a08340] hover:text-[#8a5a06] hover:bg-[#f7ecd2]")
            }`}
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>
          {menuOpen && (
            <div
              data-testid="card-admin-menu"
              className={`absolute right-0 mt-1 w-56 rounded-xl overflow-hidden z-30 animate-fade-in ${
                dm ? "bg-[#252e2f] border border-white/5" : "bg-white border border-black/5"
              }`}
              style={{ boxShadow: "0 14px 32px rgba(16,40,44,0.14)" }}
            >
              {onRankFeedback && (
                <>
                  <button
                    onClick={menuAction(onRankFeedback, article, promoted ? "clear" : "promote")}
                    data-testid="rank-promote-btn"
                    data-active={promoted ? "true" : "false"}
                    title={promoted ? `${ovLabel} — click to reset to neutral` : "Promote in feed (ranking boost ×1.5, auto-expires in 72h)"}
                    className={menuItemCls()}
                  >
                    <ThumbsUp className={`w-3.5 h-3.5 ${promoted ? "text-emerald-500 fill-current" : "opacity-60"}`} />
                    {promoted ? "Clear promote" : "Promote"}
                    {promoted && <Check className="w-3 h-3 ml-auto opacity-60" />}
                  </button>
                  <button
                    onClick={menuAction(onRankFeedback, article, demoted ? "clear" : "demote")}
                    data-testid="rank-demote-btn"
                    data-active={demoted ? "true" : "false"}
                    title={demoted ? `${ovLabel} — click to reset to neutral` : "Demote in feed (ranking ×0.2, auto-expires in 72h)"}
                    className={menuItemCls()}
                  >
                    <ThumbsDown className={`w-3.5 h-3.5 ${demoted ? "text-red-500 fill-current" : "opacity-60"}`} />
                    {demoted ? "Clear demote" : "Demote"}
                    {demoted && <Check className="w-3 h-3 ml-auto opacity-60" />}
                  </button>
                  <div className={`border-t ${dm ? "border-white/5" : "border-black/5"}`} />
                </>
              )}
              {onPrOverride && (isPr || prOverridden) && (
                <>
                  <button
                    onClick={menuAction(onPrOverride, article, isPr ? false : true)}
                    data-testid="pr-override-btn"
                    data-active={isPr ? "true" : "false"}
                    title={isPr
                      ? `Segnalato come comunicato stampa ripubblicato${article.pr_signal ? ` (${article.pr_signal.join(", ")})` : ""}${article.pr_reason ? `: ${article.pr_reason}` : ""} — clicca per dire «non è PR» (torna a rilevanza normale)`
                      : `Override admin: «non è PR»${article.pr_override?.by ? ` (${article.pr_override.by})` : ""} — clicca per rimettere il flag PR`}
                    className={menuItemCls()}
                  >
                    <Megaphone className={`w-3.5 h-3.5 ${isPr ? "text-orange-500" : "opacity-60"}`} />
                    {isPr ? "Non è PR" : "Segna come PR"}
                  </button>
                  <div className={`border-t ${dm ? "border-white/5" : "border-black/5"}`} />
                </>
              )}
              {(onStarToBrief || onUnstarBrief) && (
                <button
                  onClick={menuAction(inBrief ? onUnstarBrief : onStarToBrief, article)}
                  data-testid={inBrief ? "unstar-brief" : "star-to-brief"}
                  disabled={inBrief ? !onUnstarBrief : !onStarToBrief}
                  className={menuItemCls()}
                >
                  <PenLine className={`w-3.5 h-3.5 ${inBrief ? "text-amber-500" : "opacity-60"}`} />
                  {inBrief ? "Remove from Brief" : "Add to Brief"}
                  {inBrief && <Check className="w-3 h-3 ml-auto opacity-60" />}
                </button>
              )}
              {(onStarToNewsletter || onUnstarNewsletter) && (
                <button
                  onClick={menuAction(inNewsletter ? onUnstarNewsletter : onStarToNewsletter, article)}
                  data-testid={inNewsletter ? "unstar-newsletter" : "star-to-newsletter"}
                  disabled={inNewsletter ? !onUnstarNewsletter : !onStarToNewsletter}
                  className={menuItemCls()}
                >
                  <BookOpen className={`w-3.5 h-3.5 ${inNewsletter ? "text-amber-500" : "opacity-60"}`} />
                  {inNewsletter ? "Remove from Newsletter" : "Add to Newsletter"}
                  {inNewsletter && <Check className="w-3 h-3 ml-auto opacity-60" />}
                </button>
              )}
              {(onStarToPodcast || onUnstarPodcast) && (
                <button
                  onClick={menuAction(inPodcast ? onUnstarPodcast : onStarToPodcast, article)}
                  data-testid={inPodcast ? "unstar-podcast" : "star-to-podcast"}
                  disabled={inPodcast ? !onUnstarPodcast : !onStarToPodcast}
                  className={menuItemCls()}
                >
                  <Headphones className={`w-3.5 h-3.5 ${inPodcast ? "text-amber-500" : "opacity-60"}`} />
                  {inPodcast ? "Remove from Podcast" : "Add to Podcast"}
                  {inPodcast && <Check className="w-3 h-3 ml-auto opacity-60" />}
                </button>
              )}
              {onDelete && (
                <>
                  <div className={`border-t ${dm ? "border-white/5" : "border-black/5"}`} />
                  <button
                    onClick={menuAction(onDelete, article)}
                    data-testid="article-delete-btn"
                    aria-label="Delete story"
                    className={menuItemCls(true)}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Delete story…
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}

      <div className={`p-6 ${isAdmin ? "pr-16" : ""} ${isReddit || article.is_trending || article.is_hot ? "pt-10" : ""}`}>
        {/* Category row — hidden for Reddit (replaced by top badge), shown for regular. */}
        {!isReddit && (
          <div className={`flex items-center justify-between mb-3 ${isAdmin ? "pr-10" : ""}`}>
            <span className="flex items-center gap-2 min-w-0">
              <span className={`category-tag truncate ${dm ? "text-cyan-400/70" : "text-[#006874]/80"}`}>
                {(article.category || "").trim().toLowerCase() === "custom"
                  ? (article.source || "News")
                  : labelFor(article.category)}
              </span>
              {/* iter182: PR badge (admin mode) — republished press release, minimum relevance */}
              {isAdmin && isPr && (
                <span
                  data-testid="card-pr-chip"
                  title={`Comunicato stampa ripubblicato${article.pr_signal ? ` · segnali: ${article.pr_signal.join(", ")}` : ""}${article.pr_reason ? ` · ${article.pr_reason}` : ""} — fuori da trending/viral, mai lead, rank ×0.2`}
                  className={`flex-shrink-0 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${dm ? "bg-orange-400/15 text-orange-300" : "bg-orange-50 text-orange-700"}`}
                >
                  PR
                </span>
              )}
              {/* Admin status chip */}
              {isAdmin && (promoted || demoted || isStaged) && (
                <span
                  data-testid="card-status-chip"
                  title={ovLabel || "Staged for Brief / Newsletter / Podcast"}
                  className={`flex-shrink-0 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${
                    promoted
                      ? (dm ? "bg-emerald-400/15 text-emerald-300" : "bg-emerald-50 text-emerald-700")
                      : demoted
                        ? (dm ? "bg-red-400/15 text-red-300" : "bg-red-50 text-red-600")
                        : (dm ? "bg-amber-400/15 text-amber-300" : "bg-[#f7ecd2] text-[#8a5a06]")
                  }`}
                >
                  {promoted ? "Promoted" : demoted ? "Demoted" : "Staged"}
                </span>
              )}
            </span>
            <span className={`category-tag flex-shrink-0 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>{fmt(article.publishedAt)}</span>
          </div>
        )}
        {isReddit && (
          <div className={`flex items-center justify-end mb-3 ${isAdmin ? "pr-10" : ""}`}>
            <span className={`category-tag ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>{fmt(article.publishedAt)}</span>
          </div>
        )}

        <h3 className={`editorial-heading text-xl font-medium leading-snug mb-3 group-hover:text-[#006874] transition-colors duration-200 ${dm ? "text-slate-100 group-hover:text-cyan-400" : "text-[#191c1d]"}`}>
          {article.title}
        </h3>
        <p className={`text-sm leading-relaxed mb-5 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>{clampSentence(article.summary)}</p>

        <div className={`flex items-center justify-between pt-3 border-t ${dm ? "border-white/5" : "border-black/5"}`}>
          {isViral && article.cluster_sources?.length > 1 ? (
            <span data-testid="viral-sources" className="flex items-center gap-2 max-w-[60%] min-w-0">
              <span className="flex -space-x-1.5 flex-shrink-0">
                {article.cluster_sources.slice(0, 3).map((s, idx) => (
                  <span
                    key={`${s}-${idx}`}
                    title={s}
                    className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold uppercase ring-2 ${
                      dm ? "ring-[#1a2122] bg-[#b3541e]/30 text-orange-300" : "ring-[#f3f4f5] bg-[#faeee6] text-[#b3541e]"
                    }`}
                  >
                    {(s || "?").trim()[0]}
                  </span>
                ))}
              </span>
              <span className={`category-tag truncate ${dm ? "text-slate-500" : "text-[#8a999c]"}`}>
                {article.source}
                {article.cluster_sources.length > 1 && ` + ${article.cluster_sources.length - 1} more`}
              </span>
            </span>
          ) : (
          <span
            data-testid={sourceLogoSrc ? "news-card-source-with-logo" : "news-card-source"}
            className={`category-tag truncate max-w-[60%] flex items-center gap-1.5 ${isReddit ? (dm ? "text-orange-400/70" : "text-orange-500/70") : (dm ? "text-slate-600" : "text-[#bac9cc]")}`}
          >
            {sourceLogoSrc && (
              <img
                src={sourceLogoSrc}
                alt=""
                aria-hidden="true"
                width={16}
                height={16}
                loading="lazy"
                className="w-4 h-4 rounded-sm flex-shrink-0 object-cover"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            )}
            <span className="truncate">{article.source}</span>
          </span>
          )}
          {isAdmin && (article.signal_score > 0 || article.public_clicks > 0) && (
            <span
              data-testid="signal-score-badge"
              title={`Signal score: ${article.signal_score || 0} (ticker / brief picks · ${article.public_clicks || 0} clicks)`}
              className={`flex items-center gap-1 px-1.5 py-0.5 rounded-full category-tag flex-shrink-0 ${
                dm ? "bg-amber-400/10 text-amber-300" : "bg-[#f7ecd2] text-[#8a5a06]"
              }`}
            >
              <TrendingUp className="w-2.5 h-2.5" />
              {article.signal_score || 0}
              {article.public_clicks > 0 && (
                <span className="opacity-60">·{article.public_clicks}</span>
              )}
            </span>
          )}
          {readTime >= 3 && (
            <span className={`category-tag flex-shrink-0 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>{readTime} min read</span>
          )}
          {article.originalUrl?.startsWith("http") ? (
            <button
              onClick={() => {
                trackClick(article.originalUrl);
                window.open(article.originalUrl, "_blank", "noopener,noreferrer");
              }}
              data-testid="read-original-btn"
              className={`category-tag flex items-center gap-1 flex-shrink-0 transition-colors ${dm ? "text-cyan-400 hover:text-cyan-300" : "text-[#006874] hover:text-[#004f58]"}`}>
              Read <ExternalLink className="w-3 h-3" />
            </button>
          ) : (
            <span className={`category-tag flex-shrink-0 opacity-30 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>
              No link
            </span>
          )}
        </div>

        {article.extractedTopics?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {article.extractedTopics.slice(0, 3).map((t) => (
              <span key={t} className={`px-2 py-0.5 rounded-full category-tag ${dm ? "bg-[#252e2f] text-slate-500" : "bg-white text-[#6b7a7d]"}`}>
                {t.replace("_", " ")}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsCard;
